/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../Modal/Modal';
import { GhostButton, PrimaryButton } from '../../styles/ui/Button';
import { DuplicatesTable } from '../DuplicateMovementsModal';
import { NegativeStockTable } from '../StockImpactModal';

// ---------------------------------------------------------------------------
// Modal unificado de CONFIRMAÇÃO do movimento (2026-08-16, evolução dos
// handoffs 0089/0093).
//
// Substitui o window.confirm do Salvar nas telas de criação/edição e absorve
// os dois warn guards num só lugar: a página consulta duplicidade e impacto
// de saldo FRESCOS (`checkNow` dos hooks) e abre este modal com os achados.
// Sem achado = confirmação simples; com achado = as seções de aviso aparecem
// e o botão azul assume "Registrar mesmo assim". Nada é bloqueado — os
// guards seguem informativos.
//
// Escalabilidade (decisão do Caio, 2026-08-16): cada contexto de aviso vive
// num ACCORDEON exclusivo — o banner âmbar é o gatilho, só um expande por vez
// e a tabela interna scrolla a partir de ~6 linhas. Sem isso, 10 duplicatas +
// rupturas estouravam a altura do modal e escondiam header/footer.
//
// Botões seguem G.1: confirmar = escrita real (PrimaryButton azul);
// "Revisar" = ghost.
// ---------------------------------------------------------------------------

const SummaryLine = styled.p`
   margin: 0 0 14px;
   font-size: 13.5px;
   line-height: 1.5;
   color: rgba(0, 0, 0, 0.75);

   strong {
      font-weight: 700;
   }
`;

const WarnSection = styled.div`
   margin-top: 14px;
`;

// Gatilho do accordeon — o próprio banner âmbar. Botão local (não é variante
// do kit): declara o PRÓPRIO :hover com background (G.4.1 — sem isso o hover
// herda o azul-escrita do GlobalStyles).
const AccordionTrigger = styled.button`
   width: 100%;
   display: flex;
   align-items: center;
   gap: 8px;
   text-align: left;
   margin: 0;
   padding: 8px 12px;
   border-radius: 10px;
   border: 1px solid rgba(138, 90, 0, 0.35);
   background: rgba(255, 179, 0, 0.12);
   color: #6b4a00;
   font-size: 13px;
   font-weight: 700;
   line-height: 1.45;
   cursor: pointer;

   &:hover:not(:disabled) {
      background: rgba(255, 179, 0, 0.22);
   }

   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
`;

const TriggerLabel = styled.span`
   flex: 1;
`;

// Ícone (i) com tooltip nativo no mouseover.
const InfoIcon = styled.span`
   flex: 0 0 auto;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   width: 16px;
   height: 16px;
   border-radius: 50%;
   border: 1px solid rgba(107, 74, 0, 0.55);
   font-size: 10.5px;
   font-weight: 700;
   font-style: normal;
   cursor: help;
`;

const Chevron = styled.span`
   flex: 0 0 auto;
   font-size: 11px;
   transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
   transition: transform 140ms ease;
`;

// Corpo do accordeon: tabela scrollável — 5 linhas é seguro; a partir da 6ª
// entra o scroll vertical (max-height ≈ cabeçalho + 5 linhas). O expand/
// collapse anima via max-height/opacity (corpo sempre montado; `overflow`
// vira `auto` só aberto, pra scrollbar não aparecer durante o collapse).
const AccordionBody = styled.div`
   margin-top: ${({ $open }) => ($open ? '10px' : '0')};
   max-height: ${({ $open }) => ($open ? '300px' : '0')};
   overflow-y: ${({ $open }) => ($open ? 'auto' : 'hidden')};
   opacity: ${({ $open }) => ($open ? 1 : 0)};
   /* visibility tira os links da tabela colapsada da ordem de Tab (o
      focus-trap do Modal focaria elemento invisível); o delay segura o
      hide até o fim do collapse. */
   visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
   transition:
      max-height 220ms ease,
      margin-top 220ms ease,
      opacity 180ms ease,
      visibility 0s linear ${({ $open }) => ($open ? '0s' : '220ms')};

   @media (prefers-reduced-motion: reduce) {
      transition: none;
   }
`;

function WarnAccordion({ id, openSection, onToggle, label, tooltip, children }) {
   const isOpen = openSection === id;
   return (
      <WarnSection>
         <AccordionTrigger
            type="button"
            aria-expanded={isOpen}
            onClick={() => onToggle(id)}
         >
            <TriggerLabel>⚠ {label}</TriggerLabel>
            <InfoIcon title={tooltip} aria-label={tooltip}>
               i
            </InfoIcon>
            <Chevron $open={isOpen}>▶</Chevron>
         </AccordionTrigger>
         <AccordionBody $open={isOpen} aria-hidden={!isOpen}>
            {children}
         </AccordionBody>
      </WarnSection>
   );
}

WarnAccordion.propTypes = {
   id: PropTypes.string.isRequired,
   openSection: PropTypes.string,
   onToggle: PropTypes.func.isRequired,
   label: PropTypes.string.isRequired,
   tooltip: PropTypes.string.isRequired,
   children: PropTypes.node,
};

export default function MovementConfirmModal({
   isOpen,
   title = 'Confirmar registro do movimento',
   subtitle = null,
   itemsCount = 0,
   duplicates = [],
   negativeStock = [],
   confirmLabel,
   confirmWithWarningsLabel,
   onCancel,
   onConfirm,
}) {
   const hasWarnings = duplicates.length > 0 || negativeStock.length > 0;

   const resolvedConfirmLabel = hasWarnings
      ? (confirmWithWarningsLabel ?? 'Registrar mesmo assim')
      : (confirmLabel ?? 'Confirmar registro');

   // Accordeon EXCLUSIVO: um contexto expandido por vez (dois abertos foi o
   // que estourava a altura do modal). Ao abrir o modal, TODOS começam
   // colapsados — os títulos com contagem já resumem os achados.
   const [openSection, setOpenSection] = useState(null);

   useEffect(() => {
      if (isOpen) setOpenSection(null);
   }, [isOpen]);

   const toggleSection = (id) =>
      setOpenSection((current) => (current === id ? null : id));

   const duplicatesLabel =
      duplicates.length === 1
         ? `Possível duplicata de movimento (1)`
         : `Possíveis duplicatas de movimentos (${duplicates.length})`;

   const stockLabel =
      negativeStock.length === 1
         ? `Ruptura de estoque (1)`
         : `Rupturas de estoque (${negativeStock.length})`;

   return (
      <Modal
         isOpen={isOpen}
         title={title}
         subtitle={subtitle}
         onClose={onCancel}
         footer={
            <>
               <GhostButton type="button" onClick={onCancel}>
                  Revisar
               </GhostButton>
               <PrimaryButton type="button" onClick={onConfirm}>
                  {resolvedConfirmLabel}
               </PrimaryButton>
            </>
         }
         maxWidth="760px"
      >
         <SummaryLine>
            {itemsCount > 0 ? (
               <>
                  Movimento com <strong>{itemsCount}</strong> ite
                  {itemsCount > 1 ? 'ns' : 'm'}.
               </>
            ) : null}{' '}
            {hasWarnings
               ? 'Confira os avisos abaixo antes de confirmar — o registro não é bloqueado.'
               : 'Deseja confirmar o registro?'}
         </SummaryLine>

         {duplicates.length > 0 && (
            <WarnAccordion
               id="duplicates"
               openSection={openSection}
               onToggle={toggleSection}
               label={duplicatesLabel}
               tooltip="Movimentos ativos com o mesmo PDV, tipo e número de documento"
            >
               <DuplicatesTable duplicates={duplicates} />
            </WarnAccordion>
         )}

         {negativeStock.length > 0 && (
            <WarnAccordion
               id="stock"
               openSection={openSection}
               onToggle={toggleSection}
               label={stockLabel}
               tooltip="Produtos que ficarão com saldo zerado ou negativo após o registro do movimento"
            >
               <NegativeStockTable negativeStock={negativeStock} />
            </WarnAccordion>
         )}
      </Modal>
   );
}

MovementConfirmModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   title: PropTypes.string,
   subtitle: PropTypes.node,
   itemsCount: PropTypes.number,
   duplicates: PropTypes.arrayOf(PropTypes.shape({})),
   negativeStock: PropTypes.arrayOf(PropTypes.shape({})),
   confirmLabel: PropTypes.string,
   confirmWithWarningsLabel: PropTypes.string,
   onCancel: PropTypes.func.isRequired,
   onConfirm: PropTypes.func.isRequired,
};
